/**
 * useDreamScanDraft: the Review scans form's inputs (the cap, the rules,
 * the run days, and the staff-only run picking and second review
 * limits), their validation, and
 * whether anything differs from what is saved. The form component keeps
 * only the mutations and the markup.
 *
 * `includeLimits` says whether the second review limits are on the page
 * and in the save (TrainLoop staff). A customer's save leaves them out,
 * so their validation must not gate it: a stored value the page cannot
 * show as valid (a hand edit past the range) would otherwise disable the
 * Save for a customer who cannot see the box or the error.
 */

import { useState } from 'react'

import type { DreamScanSettings, DreamScanSettingsSelection } from '@/api/generated/model'

import { runDaysError, toggleRunDay, type RunDay } from './runDays'
import {
  draftFromSettings,
  isDraftDirty,
  newScanRule,
  validateCap,
  type DreamScanDraft,
  type ScanRule,
} from './scanRules'
import {
  limitsValid,
  validateLimits,
  type LimitErrors,
  type LimitName,
  type TeamLimitsDraft,
} from './teamLimits'

export interface DreamScanDraftState {
  draft: DreamScanDraft
  errors: { cap: string | null; rules: string | null; days: string | null; limits: LimitErrors }
  dirty: boolean
  /** Every field the save sends passes validation. */
  valid: boolean
  setCapText: (capText: string) => void
  setRules: (update: (prev: ScanRule[]) => ScanRule[]) => void
  addRule: () => void
  toggleDay: (day: RunDay) => void
  setSelection: (selection: DreamScanSettingsSelection) => void
  setLimit: (name: LimitName, text: string) => void
  discard: () => void
}

export function useDreamScanDraft(
  settings: DreamScanSettings,
  includeLimits: boolean,
): DreamScanDraftState {
  const saved = draftFromSettings(settings)
  const [capText, setCapText] = useState(saved.capText)
  const [rules, setRules] = useState<ScanRule[]>(saved.rules)
  const [runDays, setRunDays] = useState<RunDay[]>(saved.runDays)
  const [selection, setSelection] = useState<DreamScanSettingsSelection>(saved.selection)
  const [limits, setLimits] = useState<TeamLimitsDraft>(saved.limits)

  const draft: DreamScanDraft = { capText, rules, runDays, selection, limits }
  const errors = {
    cap: validateCap(capText),
    rules: rules.some((rule) => rule.value.trim() === '')
      ? 'Every rule needs a value to match.'
      : null,
    days: runDaysError(runDays),
    limits: validateLimits(limits),
  }

  return {
    draft,
    errors,
    dirty: isDraftDirty(saved, draft),
    valid:
      errors.cap === null &&
      errors.rules === null &&
      errors.days === null &&
      (!includeLimits || limitsValid(errors.limits)),
    setCapText,
    setRules,
    addRule: () => {
      setRules((prev) => [...prev, newScanRule()])
    },
    toggleDay: (day) => {
      setRunDays((prev) => toggleRunDay(prev, day))
    },
    setSelection,
    setLimit: (name, text) => {
      setLimits((prev) => ({ ...prev, [name]: text }))
    },
    discard: () => {
      setCapText(saved.capText)
      setRules(saved.rules)
      setRunDays(saved.runDays)
      setSelection(saved.selection)
      setLimits(saved.limits)
    },
  }
}
